import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
//import DialogContentText from '@mui/material/DialogContentText';
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useApi } from "../../../utils/Api";
import Preloader from "../../Preloader";
import EPortAvatar from "../../EPortAvatar";
import { DatePicker } from "@mui/x-date-pickers";
import { getCurrentDateMask, getCurrentInputFormat, toSameDateTimeUTC } from "../../../utils/dateTimeFormat";
import Translate from "../../../utils/Translate";
import * as Yup from "yup";
import debounce from "lodash/debounce";
import YesOrNoDialog from "../../YesOrNoDialog";
import ActorAvatar from "../../ActorAvatar";
import { useSnackbar } from "notistack";
import AutocompleteSearchField from "../../AutocompleteSearchField";
import ActorAutocompleteOption from "../../ActorAutocompleteOption";
import { actorSearch, actorPersonTypes } from "../../../utils/actorSearch";
import {
  tryCreatePersonnummerWithSettings,
  getPersonnummerPlaceholderText,
  setFetchOrgNoSettingsForPermitResponsibleView,
} from "../../../utils/personnummer";
import { hasAccess, accessKeys, hasSomeAccess } from "../../../utils/userAccess";

import { validatePhoneNumber } from "../../../utils/phoneNumber";
import PhoneNumberField from "../../PhoneNumberField";
import DuplicateActorMerger from "../duplicates/DuplicateActorMerger";
import { validateEmail } from "../../../utils/email";
import Personnummer from "personnummer";
import {
  getHolderActor,
  getHolderActorForResponsibleView,
  postActorForResponsibleView,
} from "../../confirmPermits/permitApi";
import { nameValidationRegex } from "../../../utils/nameValidation";
import { Clear, ExpandMore } from "@mui/icons-material";

// Get the text for submit button when saving a new actor
function getAddActorSubmitText(actorRole) {
  switch (actorRole) {
    case "teacher":
      return Translate.get("AddInstructor");
    case "student":
      return Translate.get("AddStudent");
    case "contact":
      return Translate.get("AddContact");
    case "permit-holder":
      return Translate.get("AddPerson2"); // Lägg till innehavare
    default:
      return Translate.get("AddPerson");
  }
}

// Get the text for submit button when saving an existing actor
function getUpdateActorSubmitText(actorRole, isEditOnly, hasSwitchedActor, actorId) {
  // isEditOnly determines whether we're only editing an actor or also selecting an actor for a purpose.
  // For example, consider the difference between simply editing an instructor via the list in settings,
  // and editing an instructor via a specific course. In the latter case we want a change of actor in
  // this dialog to change the instructor for the course too, and make that effect clear to the user.
  if (isEditOnly || (actorId > 0 && !hasSwitchedActor)) {
    return Translate.get("Save");
  } else {
    switch (actorRole) {
      case "teacher":
        return Translate.get("SelectInstructor");
      case "student":
        return Translate.get("SelectStudent");
      case "contact":
        return Translate.get("SelectContact");
      case "permit-holder":
        return Translate.get("SelectHolder"); // Välj innehavare
      default:
        return Translate.get("SelectPerson");
    }
  }
}

const PersonCrud = ({
  actorId,
  parentActorId,
  name,
  actorRole,
  isEditOnly,
  isMatchingActorsDisabled,
  onActorSaved,
  onClose,
  onCancel,
  hideHeader,
  parentZIndexOverride,
  checkDuplicatesOnSave,
  dataId = null, // UGLY: när dialogen används i permit responsible view
  showInPermitResponsibleView = false, // UGLY: när dialogen används i permit responsible view
}) => {
  if (
    (actorRole === "student" || actorRole === "contact" || actorRole == "permit-holder") &&
    (!parentActorId || parentActorId <= 0)
  ) {
    throw new Error("Missing parentActorId");
  }

  const [originalEmail, setOriginalEmail] = useState();
  const [hasEmailChanged, setHasEmailChanged] = useState(false);
  const [originalPersonalNo, setOriginalPersonalNo] = useState();
  const [hasPersonalNoChanged, setHasPersonalNoChanged] = useState(false);
  const [matchingActor, setMatchingActor] = useState(null);
  const [submitText, setSubmitText] = useState("OK");
  const [headerText, setHeaderText] = useState(Translate.get("EditPersonalInfo"));
  const [formData, setFormData] = useState({});
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [errorMess, setErrorMess] = useState({});
  const [isBlankForm, setIsBlankForm] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);
  const [imageResponse, setImageResponse] = useState(null);
  const [showActorWithUserEmailQuestion, setShowActorWithUserEmailQuestion] = useState(false);
  const [showActorWithUserOrgNoQuestion, setShowActorWithUserOrgNoQuestion] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [showDuplicateQuestion, setShowDuplicateQuestion] = useState(false);
  const [showDuplicateMerger, setShowDuplicateMerger] = useState(false);
  const [duplicateActor, setDuplicateActor] = useState(null);
  const [isPrivateClient, setIsPrivateClient] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [paymentAgreements, setPaymenAgreements] = useState([]);

  const [personnummerPlaceholderText, setPersonnummerPlaceholderText] = useState("");
  const [invalidOrgNoMessage, setInvalidOrgNoMessage] = useState("");
  const [newActorForPermitsMessages, setNewActorForPermitsMessages] = useState([]);
  const [oldInvalidPersonalNumber, setOldInvalidPersonalNumber] = useState(null);
  const [countries, setCountries] = useState([]);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();

  const schema = Yup.object({
    required: Yup.string().min(1, Translate.get("ePortNameRequired")).optional(),
    email: Yup.string().email(Translate.get("InvalidEmail")).optional(),
    emailRequired: Yup.string().email(Translate.get("InvalidEmail")).optional(),
    validChars: Yup.string().matches(nameValidationRegex, Translate.get("InvalidCharacters")),
    mobilePhone: Yup.string()
      .test("mobilePhone", Translate.get("InvalidPhoneNumber"), function (item) {
        if (!item) {
          return true;
        }

        let validationResult = validatePhoneNumber(item, true);
        return validationResult;
      })
      .optional(),
  });

  useEffect(() => {
    if (showInPermitResponsibleView) {
      // ugly: behöver ställa om att använda permitApi som har cookie auth
      setFetchOrgNoSettingsForPermitResponsibleView(dataId);
    }
    getPersonnummerPlaceholderText().then((result) => setPersonnummerPlaceholderText(result));
  }, []);

  const closeAndReload = () => {
    //Soft reload with redirect
    const hash = window.location.hash.endsWith("#dialog")
      ? window.location.hash.substring(0, window.location.hash.length - 7)
      : window.location.hash;
    const loc = encodeURIComponent(`${location.pathname}${location.search}${hash}`);
    navigate(`/redirect/${loc}`, { replace: true });
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      closeAndReload();
    }
  };

  const countrySearch = useCallback(
    async (searchText) => {
      const lang = localStorage.lang ?? 29;
      const search = encodeURIComponent(searchText);
      const url = `${import.meta.env.VITE_MAIN_URL}countries?searchText=${search}&languageCode=${lang}`;
      const result = await api.fetch(url, false, "GET");
      result.sort((a, b) => (a.alpha2 === "SE" ? -1 : b.alpha2 === "SE" ? 1 : a.name.localeCompare(b.name)));
      return result;
    },
    [api],
  );

  useEffect(() => {
    countrySearch("").then((result) => setCountries(result));
  }, [countrySearch]);

  const actorNameAutocompleteSearch = useCallback(async (searchText) => {
    const foundActors = await actorSearch(searchText, actorPersonTypes, ["ActorName"], 50);
    if (Array.isArray(foundActors)) {
      return foundActors.map((actor) => ({
        actorId: actor.actorId,
        actorName: actor.actorName,
        orgNo: actor.orgNo,
        email: actor.email,
        companies: actor.parentActors
          ?.filter(
            (x, index, allParentActors) =>
              x.parentActor.actorId > 0 &&
              x.parentActor.actorId !== actor.actorId &&
              allParentActors.findIndex((y) => y.parentActor.actorId === x.parentActor.actorId) === index,
          )
          .map((x) => x.parentActor.actorName),
      }));
    }

    return [];
  }, []);

  const debouncedExactMatchActorSearch = useMemo(
    () =>
      debounce(async (searchText, searchFields) => {
        const isValidSearchText = !!searchText && typeof searchText === "string" && searchText.trim() !== "";
        if (!isMatchingActorsDisabled && isValidSearchText) {
          var result = await actorSearch(
            searchText,
            actorPersonTypes,
            searchFields,
            1,
            true,
            null,
            showInPermitResponsibleView,
            dataId,
          );
          if (!!result && result.length > 0) {
            setMatchingActor(result[0]);
          }
        }
      }, 700),
    [isMatchingActorsDisabled],
  );

  async function loadCurrencies() {
    const data = await api.fetch(`${import.meta.env.VITE_MAIN_URL}orders/currencies`);
    setCurrencies(data);
  }

  async function loadPaymentAgreements() {
    if (!hasAccess(accessKeys.isMASystem)) {
      return;
    }

    const data = await api.fetch(`${import.meta.env.VITE_MAIN_URL}orders/paymentagreements`);
    const defaultPaymentAgreement = data.paymentAgreements.find((x) => !!x.isDefaultValue);
    const paymentAgreements = defaultPaymentAgreement
      ? [
          {
            ePortCodeId: "defaultPaymentAgreement",
            description: `Standard (${defaultPaymentAgreement.description})`,
          },
          ...data.paymentAgreements,
        ]
      : data.paymentAgreements;
    setPaymenAgreements(paymentAgreements);
  }

  const loadActor = async (id) => {
    const data = showInPermitResponsibleView
      ? await getHolderActorForResponsibleView(id, dataId)
      : await api.fetch(`${import.meta.env.VITE_MAIN_URL}actors/${id}/as-person`, false, "GET");

    if (data) {
      const existingActorFormData = {
        actorId: data.actorId,
        actorName: data.actorName,
        externalActorNo: data.externalActorNo,
        title: data.title,
        // For some reason null BirthDates are set to empty
        // string, so converting back to null in that case
        birthDate: data.birthDate || null,
        orgNo: data.orgNo,
        actorEmail: data.actorEmail,
        isInactive: data.isInactive,
        isConnectedToUser: data.isConnectedToUser,
        isConnectedToUserAtClient: data.isConnectedToUserAtClient,
        isConnectedToUserAtIssuer: data.isConnectedToUserAtIssuer,
        profilePictureUrl: data.profilePictureUrl,
        observationNote: data.observationNote,
        defaultCustomerOrderNo: data.defaultCustomerOrderNo,
        defaultGoodsMarking: data.defaultGoodsMarking,
        hasApprovedGDPR: data.hasApprovedGDPR,
        gdprApprovalDate: data.gdprApprovalDate,
        mobilePhone: {
          actorId: data.actorId,
          actorPhoneNumber: data.mobilePhone ? data.mobilePhone.actorPhoneNumber : null,
        },
        postalAddress: {
          actorId: data.actorId,
          actorAddressId: data.postalAddress?.actorAddressId,
          actorAddressStreet1: !!data.postalAddress?.actorAddressStreet1
            ? data.postalAddress.actorAddressStreet1
            : data.actorName,
          actorAddressStreet2: data.postalAddress?.actorAddressStreet2,
          actorAddressCity: data.postalAddress?.actorAddressCity,
          actorAddressZipCode: data.postalAddress?.actorAddressZipCode,
          actorAddressCountry: data.postalAddress?.actorAddressCountry,
        },
        invoiceAddress: !!data.invoiceAddress
          ? {
              actorId: data.actorId,
              actorAddressId: data.invoiceAddress.actorAddressId,
              actorAddressStreet1: !!data.invoiceAddress.actorAddressStreet1
                ? data.invoiceAddress.actorAddressStreet1
                : data.actorName,
              actorAddressStreet2: data.invoiceAddress.actorAddressStreet2,
              actorAddressCity: data.invoiceAddress.actorAddressCity,
              actorAddressZipCode: data.invoiceAddress.actorAddressZipCode,
              actorAddressCountry: data.invoiceAddress.actorAddressCountry,
            }
          : undefined,
        nationality: data.nationality,
        vatType: data.vatType,
      };
      setIsPrivateClient(!!data.actorTypeIds && data.actorTypeIds.includes(9));
      setOriginalEmail(data.actorEmail);
      setHasEmailChanged(false);
      setOriginalPersonalNo(data.orgNo);
      setHasPersonalNoChanged(false);
      setIsBlankForm(false);
      setFormData(existingActorFormData);
      setDataFetched(true);
    }
  };

  const uploadImage = (actorId, imageId) => {
    return api.fetch(`${import.meta.env.VITE_IMAGE_SERVICE_URL}UploadActorImageDatabase`, {
      actorId: actorId,
      imageId: imageId,
    });
  };

  const deleteImage = async (actorId) => {
    const response = await api.fetch(`${import.meta.env.VITE_IMAGE_SERVICE_URL}DeleteActorImageDatabase`, {
      actorId: actorId,
    });
    if (response?.isSuccessful !== false) {
      delete formData.profilePictureUrl;
      setFormData({ ...formData });
    }
  };

  const submitFormData = async (formData) => {
    if (isMatchingActorsDisabled && actorId > 0 && formData.actorId !== actorId) {
      // Just a sanity check, to avoid mistakes when making future changes
      throw new Error("Invalid actorId");
    }

    delete formData.profilePictureUrl;
    let endPoint = `${import.meta.env.VITE_MAIN_URL}actors/`;
    if (actorRole === "teacher") {
      endPoint = `${import.meta.env.VITE_MAIN_URL}actors/teachers/`;
    } else if (actorRole === "student") {
      endPoint = `${import.meta.env.VITE_MAIN_URL}actors/${parentActorId}/students?checkNewDuplicates=true`;
    } else if (actorRole === "permit-holder") {
      endPoint = `${import.meta.env.VITE_MAIN_URL}actors/${parentActorId}/students/`;
    } else if (actorRole === "contact") {
      endPoint = `${import.meta.env.VITE_MAIN_URL}actors/clients/${parentActorId}/contacts/`;
    }

    const data = showInPermitResponsibleView
      ? await postActorForResponsibleView(dataId, parentActorId, formData)
      : await api.fetch(endPoint, formData);

    if (data && data.isSuccessful) {
      if (imageResponse?.imageId) {
        await uploadImage(data.actorId, imageResponse.imageId);
      }

      if (onActorSaved) {
        await onActorSaved(data);
      }

      var duplicatesResponse =
        checkDuplicatesOnSave &&
        (await api.fetch(
          `${import.meta.env.VITE_MAIN_URL}actors/${data.actorId}/find-duplicates?checkNameAndCompany=false`,
          false,
          "GET",
        ));
      if (duplicatesResponse && duplicatesResponse.isSuccessful && duplicatesResponse.duplicatePersons.length > 0) {
        setDuplicateActor(duplicatesResponse.duplicatePersons[0]);
        setShowDuplicateQuestion(true);
      } else {
        handleClose();
      }
    }
    setIsSubmitLoading(false);
  };

  function validateInput(input, value, validator) {
    value = value ?? "";
    let result;
    try {
      result = schema.validateSync({ [validator]: value }) && false;
    } catch (err) {
      if (err.errors) {
        result = err.errors[0];
      }
    }

    setErrorMess((oldValue) => ({
      ...oldValue,
      [input]: result,
    }));

    validateFieldsForNewActorPermit();

    return result;
  }

  const countryIsRequired = useCallback(
    (actorAddress) => {
      if (!isPrivateClient || !hasAccess(accessKeys.isMASystem) || !actorAddress) return false;

      return Boolean(
        actorAddress.actorAddressStreet2?.trim() ||
        actorAddress.actorAddressZipCode?.trim() ||
        actorAddress.actorAddressCity?.trim(),
      );
    },
    [isPrivateClient],
  );

  const checkCountryRequired = useCallback(
    (input, actorAddress) => {
      setErrorMess((oldValue) => ({
        ...oldValue,
        [input]:
          countryIsRequired(actorAddress) && !actorAddress?.actorAddressCountry?.trim()
            ? Translate.get("Compulsory")
            : false,
      }));
    },
    [countryIsRequired],
  );

  function stringIsEmpty(str) {
    return !str || str.length < 1;
  }

  function validateFieldsForNewActorPermit() {
    let newActorForPermitsErrors = [];
    if (showInPermitResponsibleView) {
      const email = formData.actorEmail;
      const mobile = formData.mobilePhone?.actorPhoneNumber;
      const personalNo = formData.orgNo;

      const invalidEmail = stringIsEmpty(email) || !validateEmail(email);
      const invalidMobile = stringIsEmpty(mobile) || !validatePhoneNumber(mobile, true);
      // const formattedPersonalNumber = stringIsEmpty(personalNo) || personalNo.length < 10 ? { isValid: false} : tryCreatePersonnummerWithSettings(personalNo);
      const tmp = Personnummer.valid(personalNo);
      const invalidPersonalNumber =
        stringIsEmpty(personalNo) || personalNo.length < 10 || !Personnummer.valid(personalNo);

      if (invalidEmail && invalidMobile) {
        newActorForPermitsErrors = [...newActorForPermitsErrors, Translate.get("ValidEmailOrPhoneRequired")];
      }

      if (invalidPersonalNumber) {
        newActorForPermitsErrors = [...newActorForPermitsErrors, Translate.get("ValidateCorrectSSN")];
      }

      // if(!stringIsEmpty(formattedPersonalNumber) && formattedPersonalNumber !== personalNo) {
      //   setFormData({...formData, orgNo: formattedPersonalNumber});
      // }
    }

    setNewActorForPermitsMessages(newActorForPermitsErrors);
  }

  useEffect(() => {
    if (isBlankForm) {
      setSubmitDisabled(true);
    } else {
      checkCountryRequired("invoiceAddressCountry", formData.invoiceAddress);
    }
  }, [formData, isBlankForm, checkCountryRequired]);

  useEffect(() => {
    const anyErrors = Object.values(errorMess).some((x) => x) || newActorForPermitsMessages?.length > 0;
    setSubmitDisabled(anyErrors);
  }, [errorMess, newActorForPermitsMessages]);

  useEffect(() => {
    if (actorId) {
      loadActor(actorId);
      setHeaderText(Translate.get("EditPersonalInfo"));
      setSubmitText(getUpdateActorSubmitText(actorRole, isEditOnly, false, actorId));
    } else {
      const addActorSubmitText = getAddActorSubmitText(actorRole);
      setHeaderText(addActorSubmitText);
      setSubmitText(addActorSubmitText);
      const blankFormData = {
        actorName: name ?? null,
        externalActorNo: null,
        title: null,
        birthDate: null,
        orgNo: null,
        actorEmail: null,
        mobilePhone: { actorPhoneNumber: null },
        postalAddress: {
          actorAddressStreet2: null,
          actorAddressCity: null,
          actorAddressZipCode: null,
          actorAddressCountry: null,
        },
      };

      setOriginalEmail(null);
      setHasEmailChanged(false);
      setOriginalPersonalNo(null);
      setHasPersonalNoChanged(false);
      setIsBlankForm(!name);
      setFormData(blankFormData);
      setDataFetched(true);
    }
  }, [actorId, name, actorRole, isEditOnly]);

  useEffect(() => {
    if (isPrivateClient) {
      loadCurrencies();
      loadPaymentAgreements();
    }
  }, [isPrivateClient]);

  function handleCancelSubmit() {
    setShowActorWithUserOrgNoQuestion(false);
    setShowActorWithUserEmailQuestion(false);
  }

  const handleSubmit = (event) => {
    event.preventDefault();

    // The number is otherwise only validated on blur, which doesn't happen when submitting with Enter
    if (validateInput("actorPhoneNumber", formData.mobilePhone?.actorPhoneNumber, "mobilePhone")) {
      return;
    }

    const askAboutUserOrgNo =
      hasPersonalNoChanged &&
      formData.isConnectedToUserAtIssuer &&
      hasAccess(accessKeys.licenseIssuer) &&
      (hasAccess(accessKeys.swedishCompany) || hasAccess(accessKeys.isMASystem));
    if (askAboutUserOrgNo) {
      setShowActorWithUserOrgNoQuestion(true);
    }

    const askAboutUserEmail = hasEmailChanged && formData.isConnectedToUser;
    if (askAboutUserEmail) {
      setShowActorWithUserEmailQuestion(true);
    }

    if (!askAboutUserEmail && !askAboutUserOrgNo) {
      setIsSubmitLoading(true);
      submitFormData(formData);
    }
  };

  function handleSubmitActorConnectedToUser() {
    setShowActorWithUserEmailQuestion(false);
    setShowActorWithUserOrgNoQuestion(false);
    setIsSubmitLoading(true);
    submitFormData(formData);
  }

  function handleUseMatchingActor() {
    setDataFetched(false);
    loadActor(matchingActor.actorId);
    setHeaderText(Translate.get("EditPersonalInfo"));
    setSubmitText(getUpdateActorSubmitText(actorRole, isEditOnly, true, actorId));
    setMatchingActor(null);
  }

  function handleActorNameAutocompleteChange(actor) {
    if (actor && actor.actorId > 0) {
      if (!showInPermitResponsibleView) {
        // sök inte efter actors när denna används i responsible view
        setDataFetched(false);
        loadActor(actor.actorId);
        setHeaderText(Translate.get("EditPersonalInfo"));
        setSubmitText(getUpdateActorSubmitText(actorRole, isEditOnly, true, actorId));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        actorName: actor?.actorName,
        invoiceAddress: !!prev.invoiceAddress
          ? { ...prev.invoiceAddress, actorAddressStreet1: actor?.actorName }
          : undefined,
        postalAddress: !!prev.postalAddress
          ? { ...prev.postalAddress, actorAddressStreet1: actor?.actorName }
          : undefined,
      }));
      const error = validateInput("actorName", actor?.actorName, "required");
      if (!error) {
        validateInput("actorName", actor?.actorName, "validChars");
      }
      setIsBlankForm(!actor?.actorName);
    }
  }

  function handleCancel() {
    if (onCancel) {
      onCancel();
    }
    handleClose();
  }

  function orgNoRequired() {
    return showInPermitResponsibleView;
  }

  function handleAvatarDisableSubmit(shouldDisable) {
    if (shouldDisable) {
      setSubmitDisabled(true);
    } else {
      if (!isBlankForm) {
        setSubmitDisabled(false);
      } else {
        setSubmitDisabled(true);
      }
    }
  }

  async function handleOrgNoBlur(e) {
    if (formData.orgNo === oldInvalidPersonalNumber) {
      return;
    }

    validateFieldsForNewActorPermit();

    let res = await tryCreatePersonnummerWithSettings(e.target.value);
    if (res === null) {
      return;
    }

    if (!res.isValid) {
      setOldInvalidPersonalNumber(e.target.value);
      setInvalidOrgNoMessage(Translate.get("SsnDeviate"));
    } else if (res.formattedPersonalNumber !== null) {
      formData.orgNo = res.formattedPersonalNumber;
      setInvalidOrgNoMessage("");
    }
  }

  // Using this to avoid validating the potentially empty
  // name value when opening an empty form the first time
  const isActorNameBlank = !("actorName" in formData) || typeof formData?.actorName === typeof null;

  if (showDuplicateMerger) {
    return (
      <DuplicateActorMerger
        actorId1={duplicateActor.actor1.actorId}
        actorId2={duplicateActor.actor2.actorId}
        onClose={handleClose}
        disableShowNavigateQuestion
      />
    );
  }

  console.log("errors", errorMess);

  return (
    <>
      {!dataFetched && (
        <>
          <Preloader />
          {!hideHeader && <DialogTitle>{headerText}</DialogTitle>}
          <DialogContent className="stdDialogContent"></DialogContent>
        </>
      )}
      {dataFetched && (
        <>
          {!hideHeader && <DialogTitle>{headerText}</DialogTitle>}
          <DialogContent
            dividers={true}
            className="stdDialogContent dialogOverlay"
            sx={{
              // Width of dialog shrank after I switched places for some fields, this counteracts that
              "&.stdDialogContent": {
                "@media (min-width: 701px)": {
                  minWidth: "min(697px, calc(100vw - 64px))",
                },
              },
            }}
          >
            {/* <FormControl fullWidth margin="normal" size='medium' fullWidth={true} onSubmit={handleSubmit}> */}
            <form onSubmit={handleSubmit} className="twoColForm">
              {!showInPermitResponsibleView && (
                <div className="formAvatar">
                  <div className="smallHeader">{Translate.get("ProfilePicture")}</div>
                  <EPortAvatar
                    avatarWidth={160}
                    avatarHeight={160}
                    fontSize={72}
                    displayName={formData.actorName ? formData.actorName : "?"}
                    pictureUrl={formData.profilePictureUrl + ""}
                    onImageUploaded={(resp) => setImageResponse(resp)}
                    onDeleteImage={() => deleteImage(actorId)}
                    disableSubmit={handleAvatarDisableSubmit}
                    imageType={"person"}
                  />
                </div>
              )}
              <div className="stdForm">
                {newActorForPermitsMessages?.length > 0 && (
                  <div>
                    <Alert severity="error">
                      <ul>
                        {newActorForPermitsMessages.map((msg) => (
                          <li>{msg}</li>
                        ))}
                      </ul>
                    </Alert>
                  </div>
                )}
                <div className="smallHeader">{Translate.get("PersonalInfo")}</div>
                <div>
                  <AutocompleteSearchField
                    placeholder={Translate.get("NameFieldHelptext")}
                    label={Translate.get("Name")}
                    value={{ actorName: formData.actorName ?? "" }}
                    onValueChange={handleActorNameAutocompleteChange}
                    getOptionLabel={(option) => option?.actorName ?? ""}
                    renderOption={(option) => (
                      <ActorAutocompleteOption
                        actorName={option?.actorName}
                        orgNo={option?.orgNo}
                        email={option?.email}
                        companies={option?.companies}
                      />
                    )}
                    keyPropName="actorId"
                    search={
                      !formData || !formData.actorId || formData.actorId <= 0 ? actorNameAutocompleteSearch : null
                    }
                    createNewValue={(text) => ({
                      actorName: text,
                    })}
                    textFieldProps={{
                      required: true,
                      error: !isActorNameBlank && !!errorMess["actorName"],
                      helperText:
                        (invalidOrgNoMessage.length > 0 ? " " : "") || (!isActorNameBlank && errorMess["actorName"]),
                    }}
                    hideOptionsFilter={formData.actorId > 0 && ((option) => option.actorId === formData.actorId)}
                    disablePortal={parentZIndexOverride}
                  />
                  <TextField
                    inputProps={{ maxLength: 13 }}
                    fullWidth
                    value={formData.orgNo ?? ""}
                    onChange={(event, value) => {
                      setFormData({ ...formData, orgNo: event.target.value });
                      setIsBlankForm(false);
                      setHasPersonalNoChanged(originalPersonalNo !== event.target.value);
                      debouncedExactMatchActorSearch(event.target.value, ["OrgNo"]);
                    }}
                    label={Translate.get("PersonalNo")}
                    required={orgNoRequired()}
                    placeholder={Translate.get(personnummerPlaceholderText)}
                    error={!!invalidOrgNoMessage}
                    helperText={invalidOrgNoMessage || (!isActorNameBlank && errorMess["actorName"] && " ")}
                    onBlur={handleOrgNoBlur}
                  />
                </div>
                <div>
                  <DatePicker
                    displayWeekNumber={hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem)}
                    mask={getCurrentDateMask()}
                    format={getCurrentInputFormat()}
                    value={formData.birthDate ? new Date(formData.birthDate) : null}
                    onChange={(newDate) => {
                      setFormData({
                        ...formData,
                        birthDate: toSameDateTimeUTC(newDate),
                      });
                      setIsBlankForm(false);
                    }}
                    label={Translate.get("BirthDate")}
                    slotProps={{ textField: { fullWidth: true } }}
                  />

                  <AutocompleteSearchField
                    requireSelection
                    disableAddAsNew
                    disablePortal={parentZIndexOverride}
                    label={Translate.get("Nationality")}
                    defaultOptions={countries}
                    value={
                      formData.nationality && countries && countries.length > 0
                        ? countries.find((c) => c.alpha2 === formData.nationality)
                        : ""
                    }
                    onValueChange={(option) => {
                      setFormData({
                        ...formData,
                        nationality: option?.alpha2,
                      });
                      setIsBlankForm(false);
                    }}
                    getOptionLabel={(option) => (option ? `${option.name} (${option.alpha2})` : "")}
                    renderOption={(option) => `${option.name} (${option.alpha2})`}
                    keyPropName="alpha2"
                    search={countrySearch}
                    textFieldProps={{
                      required: false,
                    }}
                    hideOptionsFilter={formData.nationality && ((option) => option.alpha2 === formData.nationality)}
                  />
                </div>
                <div>
                  <TextField
                    fullWidth
                    value={formData.externalActorNo ?? ""}
                    onChange={(event, value) => {
                      setFormData({
                        ...formData,
                        externalActorNo: event.target.value,
                      });
                      setIsBlankForm(false);
                      debouncedExactMatchActorSearch(event.target.value, ["ExternalActorNo"]);
                    }}
                    label={Translate.get("EmployeeNo")}
                  />
                  <TextField
                    fullWidth
                    value={formData.title ?? ""}
                    onChange={(event) => {
                      setFormData({
                        ...formData,
                        title: event.target.value,
                      });
                      setIsBlankForm(false);
                    }}
                    label={Translate.get("ActorTitle")}
                    inputProps={{ maxLength: 200 }}
                  />
                </div>
                <div className="smallHeader">{Translate.get("ContactInformation")}</div>
                <div>
                  <TextField
                    fullWidth
                    spellCheck={false}
                    value={formData.actorEmail ?? ""}
                    onChange={(event, value) => {
                      setFormData({
                        ...formData,
                        actorEmail: event.target.value,
                      });
                      if (errorMess.actorEmail) {
                        validateInput("actorEmail", event.target.value, "email");
                      }
                      setIsBlankForm(false);
                      setHasEmailChanged(originalEmail !== event.target.value);
                      debouncedExactMatchActorSearch(event.target.value, ["ActorEmail"]);
                    }}
                    onBlur={() => validateInput("actorEmail", formData?.actorEmail, "email")}
                    label={Translate.get("Email")}
                    error={!!errorMess["actorEmail"]}
                    helperText={errorMess["actorEmail"] ? errorMess["actorEmail"] : " "}
                  />
                  <PhoneNumberField
                    value={formData.mobilePhone.actorPhoneNumber ?? ""}
                    onPhoneNumberChange={(phoneNumber) => {
                      setFormData((prev) => ({
                        ...prev,
                        mobilePhone: {
                          actorPhoneNumber: phoneNumber,
                          actorId: prev.actorId,
                        },
                      }));
                      setIsBlankForm(false);
                      if (errorMess.actorPhoneNumber) {
                        validateInput("actorPhoneNumber", phoneNumber, "mobilePhone");
                      }
                    }}
                    onBlur={() =>
                      validateInput("actorPhoneNumber", formData?.mobilePhone?.actorPhoneNumber, "mobilePhone")
                    }
                    label={Translate.get("MobilePhone")}
                    error={!!errorMess["actorPhoneNumber"]}
                    helperText={errorMess["actorPhoneNumber"] ? errorMess["actorPhoneNumber"] : " "}
                    disablePortal={parentZIndexOverride}
                  />
                </div>
                <div className="smallHeader">{Translate.get("Address")}</div>
                <div>
                  <TextField
                    fullWidth
                    value={formData.postalAddress.actorAddressStreet2 ?? ""}
                    onChange={(event, value) => {
                      const updatedAddress = {
                        ...formData.postalAddress,
                        actorAddressStreet2: event.target.value,
                        actorId: formData.actorId,
                      };
                      setFormData({
                        ...formData,
                        postalAddress: updatedAddress,
                      });
                      validateInput("actorAddressStreet2", updatedAddress?.actorAddressStreet2, "validChars");
                      setIsBlankForm(false);
                    }}
                    error={!!errorMess["actorAddressStreet2"]}
                    helperText={errorMess["actorAddressStreet2"]}
                    label={Translate.get("StreetAddress")}
                  />
                  <TextField
                    fullWidth
                    value={formData.postalAddress.actorAddressZipCode ?? ""}
                    onChange={(event, value) => {
                      const updatedAddress = {
                        ...formData.postalAddress,
                        actorAddressZipCode: event.target.value,
                        actorId: formData.actorId,
                      };
                      setFormData({
                        ...formData,
                        postalAddress: updatedAddress,
                      });
                      validateInput("actorAddressZipCode", updatedAddress?.actorAddressZipCode, "validChars");
                      setIsBlankForm(false);
                    }}
                    error={!!errorMess["actorAddressZipCode"]}
                    helperText={errorMess["actorAddressZipCode"]}
                    label={Translate.get("ZipCode")}
                  />
                </div>
                <div>
                  <TextField
                    fullWidth
                    value={formData.postalAddress.actorAddressCity ?? ""}
                    onChange={(event, value) => {
                      const updatedAddress = {
                        ...formData.postalAddress,
                        actorAddressCity: event.target.value,
                        actorId: formData.actorId,
                      };
                      setFormData({
                        ...formData,
                        postalAddress: updatedAddress,
                      });
                      validateInput("actorAddressCity", updatedAddress?.actorAddressCity, "validChars");
                      setIsBlankForm(false);
                    }}
                    error={!!errorMess["actorAddressCity"]}
                    helperText={errorMess["actorAddressCity"]}
                    label={Translate.get("City")}
                  />

                  <TextField
                    fullWidth
                    value={formData.postalAddress.actorAddressCountry ?? ""}
                    onChange={(event, value) => {
                      const updatedAddress = {
                        ...formData.postalAddress,
                        actorAddressCountry: event.target.value,
                        actorId: formData.actorId,
                      };
                      setFormData({
                        ...formData,
                        postalAddress: updatedAddress,
                      });
                      validateInput("actorAddressCountry", updatedAddress?.actorAddressCountry, "validChars");
                      setIsBlankForm(false);
                    }}
                    error={!!errorMess["actorAddressCountry"]}
                    helperText={errorMess["actorAddressCountry"]}
                    label={Translate.get("Country")}
                  />
                </div>
                <div className="smallHeader">{Translate.get("Other")}</div>
                <div>
                  <TextField
                    fullWidth
                    value={formData.defaultCustomerOrderNo ?? ""}
                    onChange={(event) => {
                      setFormData({
                        ...formData,
                        defaultCustomerOrderNo: event.target.value,
                      });
                      setIsBlankForm(false);
                    }}
                    label={Translate.get("DefaultCustomerOrderNo")}
                    inputProps={{ maxLength: 30 }}
                  />
                </div>
                {hasAccess(accessKeys.isMASystem) && (
                  <div>
                    <TextField
                      fullWidth
                      value={formData.defaultGoodsMarking ?? ""}
                      onChange={(event) => {
                        setFormData({
                          ...formData,
                          defaultGoodsMarking: event.target.value,
                        });
                        setIsBlankForm(false);
                      }}
                      label={Translate.get("GoodsMarking")}
                      inputProps={{ maxLength: 30 }}
                    />
                  </div>
                )}
                <div>
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    value={formData.observationNote ?? ""}
                    onChange={(event) => {
                      setFormData({
                        ...formData,
                        observationNote: event.target.value,
                      });
                      setIsBlankForm(false);
                    }}
                    label={Translate.get("ObservationNote")}
                    inputProps={{ maxLength: 255 }}
                  />
                </div>
                {hasAccess(accessKeys.isMASystem) && isPrivateClient && (
                  <div style={{ width: "100%", padding: 0 }}>
                    <Accordion style={{ width: "100%", left: "-1.5rem", margin: 0 }}>
                      <AccordionSummary
                        className="smallHeader"
                        sx={{ paddingLeft: "1.3rem", width: "105%" }}
                        expandIcon={<ExpandMore />}
                      >
                        {`${Translate.get("BillingInformation")} (${Translate.get("PrivateBookingLabel")})`}
                      </AccordionSummary>
                      <AccordionDetails sx={{ paddingLeft: "1.4rem", width: "105%" }}>
                        <div class="stdForm">
                          <div>
                            <FormControl fullWidth>
                              <InputLabel id="vat-type-label">{Translate.get("VatType")}</InputLabel>
                              <Select
                                label={Translate.get("VatType")}
                                labelId="vat-type-label"
                                value={formData.vatType ?? ""}
                                onChange={(event) => {
                                  setFormData({
                                    ...formData,
                                    vatType: event.target.value,
                                  });
                                  setIsBlankForm(false);
                                }}
                              >
                                {[1, 2, 3].map((vatType) => (
                                  <MenuItem value={vatType}>{Translate.getVatType(vatType)}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </div>

                          <div className="smallHeader">{Translate.getActorAddressType(1)}</div>
                          <div>
                            <TextField
                              fullWidth
                              value={formData.invoiceAddress?.actorAddressStreet2 ?? ""}
                              onChange={(event) => {
                                const adr = {
                                  ...formData.invoiceAddress,
                                  actorAddressStreet2: event.target.value,
                                  actorId: formData.actorId,
                                };
                                setFormData({ ...formData, invoiceAddress: adr });
                                validateInput("invoiceAddressStreet2", adr?.actorAddressStreet2, "validChars");
                                setIsBlankForm(false);
                              }}
                              error={!!errorMess["invoiceAddressStreet2"]}
                              helperText={errorMess["invoiceAddressStreet2"]}
                              label={Translate.get("StreetAddress")}
                            />
                            <TextField
                              fullWidth
                              value={formData.invoiceAddress?.actorAddressZipCode ?? ""}
                              onChange={(event) => {
                                const adr = {
                                  ...formData.invoiceAddress,
                                  actorAddressZipCode: event.target.value,
                                  actorId: formData.actorId,
                                };
                                setFormData({ ...formData, invoiceAddress: adr });
                                validateInput("invoiceAddressZipCode", adr?.actorAddressZipCode, "validChars");
                                setIsBlankForm(false);
                              }}
                              error={!!errorMess["invoiceAddressZipCode"]}
                              helperText={errorMess["invoiceAddressZipCode"]}
                              label={Translate.get("ZipCode")}
                            />
                          </div>
                          <div>
                            <TextField
                              fullWidth
                              value={formData.invoiceAddress?.actorAddressCity ?? ""}
                              onChange={(event) => {
                                const adr = {
                                  ...formData.invoiceAddress,
                                  actorAddressCity: event.target.value,
                                  actorId: formData.actorId,
                                };
                                setFormData({ ...formData, invoiceAddress: adr });
                                validateInput("invoiceAddressCity", adr?.actorAddressCity, "validChars");
                                setIsBlankForm(false);
                              }}
                              error={!!errorMess["invoiceAddressCity"]}
                              helperText={errorMess["invoiceAddressCity"]}
                              label={Translate.get("City")}
                            />
                            <TextField
                              fullWidth
                              value={formData.invoiceAddress?.actorAddressCountry ?? ""}
                              onChange={(event) => {
                                const adr = {
                                  ...formData.invoiceAddress,
                                  actorAddressCountry: event.target.value,
                                  actorId: formData.actorId,
                                };
                                setFormData({ ...formData, invoiceAddress: adr });
                                validateInput("invoiceAddressCountry", adr?.actorAddressCountry, "validChars");
                                checkCountryRequired("invoiceAddressCountry", formData.invoiceAddress);
                                setIsBlankForm(false);
                              }}
                              label={Translate.get("Country")}
                              required={countryIsRequired(formData.invoiceAddress)}
                              error={!!errorMess["invoiceAddressCountry"]}
                              helperText={!!errorMess["invoiceAddressCountry"]}
                            />
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </div>
                )}
                <Tooltip
                  placement="top-end"
                  title={`${Translate.get("PersonIsActive")} : ${
                    !formData.isInactive ? Translate.get("Yes") : Translate.get("No")
                  }`}
                >
                  <Stack justifyContent="space-between" direction="row" disableGutters>
                    <Container
                      disableGutters
                      sx={{
                        paddingTop: ".5rem",
                        //paddingLeft:".7rem"
                      }}
                      onClick={() =>
                        setFormData({
                          ...formData,
                          isInactive: !formData.isInactive,
                        })
                      }
                    >
                      {Translate.get("PersonIsActive")}
                    </Container>
                    <Switch
                      checked={!formData.isInactive}
                      name="companyIsActive"
                      onChange={(evt) =>
                        setFormData({
                          ...formData,
                          isInactive: !evt.target.checked,
                        })
                      }
                    />
                  </Stack>
                </Tooltip>
              </div>
            </form>
            {/* </FormControl> */}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel}>{Translate.get("Cancel")}</Button>
            <Button
              className="stdLightLoader"
              loading={isSubmitLoading}
              disabled={submitDisabled}
              onClick={handleSubmit}
            >
              {submitText}
            </Button>
          </DialogActions>
          {/* YesOrNoDialog for asking if we should merge a duplicate found after saving */}
          <YesOrNoDialog
            open={showDuplicateQuestion}
            title={Translate.get("DuplicateFoundMergeNowTitle")}
            content={Translate.get("DuplicateFoundMergeNowQuestion")}
            onNo={() => {
              setShowDuplicateQuestion(false);
              handleClose();
            }}
            onYes={() => {
              setShowDuplicateQuestion(false);
              setShowDuplicateMerger(true);
            }}
            noText={Translate.get("No")}
            yesText={Translate.get("Yes")}
          />
          {/* YesOrNoDialog for asking if we should use an existing matching actor instead */}
          <YesOrNoDialog
            open={!!matchingActor && matchingActor.actorId !== formData.actorId}
            title={Translate.get("UseMatchingPersonTitle")}
            content={
              !!matchingActor ? (
                <>
                  {Translate.get("UseMatchingPersonQuestion")}
                  <Box marginTop="24px">
                    <ActorAvatar
                      name={matchingActor.actorName}
                      email={matchingActor.email}
                      extraFields={[
                        matchingActor.orgNo,
                        matchingActor.externalActorNo,
                        matchingActor.parentActors
                          ? [...matchingActor.parentActors.map((parent) => parent.parentActor.actorName)]
                          : [],
                      ]}
                      profilePictureUrl={matchingActor.profilePictureUrl}
                      vertical
                    />
                  </Box>
                </>
              ) : (
                ""
              )
            }
            onNo={() => setMatchingActor(null)}
            onYes={handleUseMatchingActor}
            noText={Translate.get("No")}
            yesText={Translate.get("Yes")}
          />
          {/* YesOrNoDialog for verifying that we should update an actor connected to a user.
              There may be more than one question, so they need to be displayed sequentially */}
          <YesOrNoDialog
            open={showActorWithUserEmailQuestion}
            title={Translate.get("UpdatePersonConnectedToUserTitle")}
            content={Translate.get("UpdatePersonConnectedToUserQuestion")}
            onNo={handleCancelSubmit}
            onYes={() =>
              showActorWithUserOrgNoQuestion
                ? setShowActorWithUserEmailQuestion(false)
                : handleSubmitActorConnectedToUser()
            }
            noText={Translate.get("No")}
            yesText={Translate.get("Yes")}
          />
          <YesOrNoDialog
            open={showActorWithUserOrgNoQuestion && !showActorWithUserEmailQuestion}
            title={Translate.get("UpdatePersonConnectedToUserOrgNoTitle")}
            content={Translate.get("UpdatePersonConnectedToUserOrgNoQuestion")}
            onNo={handleCancelSubmit}
            onYes={handleSubmitActorConnectedToUser}
            noText={Translate.get("No")}
            yesText={Translate.get("Yes")}
          />
          {/* Allt innehåll */}
        </>
      )}
    </>
  );
};
export default PersonCrud;
